/** CSV export button for CDRs. */
import React from "react";
import { Button, Snackbar, Alert } from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";
import { cdrsApi } from "../../api/client";

interface ExportButtonProps {
  queryParams: {
    start_date: string;
    end_date: string;
    carrier_id?: string;
    status?: string;
  };
  disabled?: boolean;
}

/** Export button that downloads CDRs as CSV file. */
export default function ExportButton({
  queryParams,
  disabled = false,
}: ExportButtonProps) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleExport = async () => {
    try {
      setLoading(true);
      setError(null);

      // Request CSV export from API
      const response = await cdrsApi.export(queryParams);

      // Get filename from Content-Disposition header or generate default
      const contentDisposition = response.headers["content-disposition"];
      let filename = "cdrs_export.csv";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match && match[1]) {
          filename = match[1].replace(/"/g, "");
        }
      }

      // Create blob from response data
      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8",
      });

      // Create download URL and trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Export failed:", err);
      // C5: surface export failures instead of failing silently
      setError(
        err?.response?.data?.detail ||
          "Export failed. Please check your connection and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<DownloadIcon />}
        onClick={handleExport}
        disabled={disabled || loading}
        size="small"
        aria-label="Export CDRs as CSV"
      >
        {loading ? "Exporting..." : "Export CSV"}
      </Button>

      <Snackbar
        open={error !== null}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
